import CTA from 'components/Campaign/CTA';
import EmptyResults from 'components/Campaign/EmptyResults';
import { Petition } from 'components/cards';
import { Letter } from 'components/cards/letter';
import PersonPhoto from 'components/cards/PersonPhoto';
import { Poll } from 'components/cards/poll';
import Counter from 'components/counter';
import { Chart } from 'components/delivery-charts/Charts';
import RangeSelector from 'components/RangeSelector/RangeSelector';
import Anchor from 'components/typography/anchor';
import petitionIcon from 'images/icons/clipboard-pen-line.svg';
import letterIcon from 'images/icons/inbox.svg';
import pollIcon from 'images/icons/line-chart.svg';
import votizen from 'images/votizen.svg';
import Img from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { DATA_TYPES } from 'siteconfig';
import { CampaignTypeList, OpenLetterTypeList } from 'utilities/propTypes';
import { getStateProperName } from 'utilities/states';

const REVIEWS_ID = 'reviews'; // Just to make sure we're using this consistently in the selectors
const LETTER_TYPES = [
  { id: 'petitions', label: 'Petitions', icon: petitionIcon },
  { id: 'letters', label: 'Letters', icon: letterIcon },
  {
    id: REVIEWS_ID,
    label: 'Reviews',
    icon: pollIcon,
  },
];

const PersonPage = ({ petitions, openLetters, person }) => {
  // Prepare the displayed sections
  // petitions
  const petitionSection = petitions.map((p) => (
    <Petition key={p.petition_code} petition={p} />
  ));

  // Individual letters
  const letterSection = openLetters.map((l) => (
    <Letter key={l.correlationId} letter={l} />
  ));

  // Polls/votes
  const votesSection = person.approval.pollVotes.map((vote) => (
    <Poll key={vote.id} vote={vote} />
  ));

  // State management
  const [selectedRangeId, setSelectedRangeId] = useState('letters');

  const router = useRouter();
  const { type } = router.query;

  useEffect(() => {
    if (LETTER_TYPES.some((letterType) => letterType.id === type)) {
      setSelectedRangeId(type);
    }
  }, [type]);

  // Filter selection change handler
  const handleRangeChange = (e) => {
    setSelectedRangeId(e.target.id);
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, type: e.target.id },
      },
      undefined,
      { shallow: true }
    );
  };

  let selectedSet;
  if (selectedRangeId === 'letters') {
    selectedSet =
      letterSection.length > 0 ? (
        <>
          <div className="flex flex-col space-y-1">{letterSection}</div>
          <div className="m-10 flex flex-col gap-2 rounded-sm bg-transparent text-center">
            <p>
              Write a letter and send it instantly by email, fax, or postal
              mail. It takes two minutes to be&nbsp;heard.
            </p>
            <CTA
              className="mx-auto"
              keyword="write"
              copy={`Write to ${person.fullName}`}
            />
          </div>
        </>
      ) : (
        <EmptyResults type={DATA_TYPES.LETTER} />
      );
  } else if (selectedRangeId === 'petitions') {
    selectedSet =
      petitionSection.length > 0 ? (
        <div className="flex flex-col space-y-1">{petitionSection}</div>
      ) : (
        <EmptyResults type={DATA_TYPES.PETITION} />
      );
  } else if (selectedRangeId === REVIEWS_ID) {
    selectedSet =
      votesSection.length > 0 ? (
        <>
          <div className="flex flex-col space-y-1">{votesSection}</div>
          <div className="m-10 flex flex-col gap-2 rounded-sm bg-transparent text-center">
            <p>
              Do you approve of how {person.fullName} is performing their job in
              office? Be heard.
            </p>
            <CTA
              className="mx-auto"
              keyword="review"
              copy={`Review ${person.fullName}`}
            />
          </div>
        </>
      ) : (
        <EmptyResults type={DATA_TYPES.REVIEW} />
      );
  }

  return (
    <>
      <div className="mb-4 flex flex-col items-center gap-4 text-center">
        <PersonPhoto
          bioguideId={person.bioguideId}
          name={person.fullName}
          officialType={person.officialType}
          size={'large'}
        />
        <section className="grow">
          <ol
            aria-label="breadcrumbs"
            className="mb-2 flex flex-wrap justify-center gap-2 text-sm leading-tight tracking-wider uppercase"
          >
            <li className="after:content-['→']">
              <Anchor
                className="mr-2 anchor-subtle whitespace-nowrap"
                href="/letters"
              >
                United States
              </Anchor>
            </li>
            {person.stateCode && (
              <li className="after:content-['→']">
                <Anchor
                  className="mr-2 anchor-subtle whitespace-nowrap"
                  href={`/petitions/explore/${person.stateCode}`}
                >
                  {getStateProperName(person.stateCode)}
                </Anchor>
              </li>
            )}
            <li aria-current="page" className="whitespace-nowrap">
              {`${person.titleAbbreviation} ${person.fullName}`}
            </li>
          </ol>
          <h1>{`${person.titleAbbreviation} ${person.fullName}`}</h1>
          {person.amendmentCandidate ? (
            <div className="mx-auto mt-4 flex max-w-prose items-center gap-4 rounded-lg border border-stone-200 bg-stone-50 p-4 text-left text-sm dark:border-stone-700 dark:bg-stone-800/50">
              <Img
                src={votizen}
                alt=""
                width={36}
                height={36}
                className="shrink-0"
              />
              <p>
                {person.fullName} is on the 2026 ballot.{' '}
                <Anchor
                  className="anchor-text"
                  href={person.amendmentCandidate.url}
                >
                  See their campaign on Amendment →
                </Anchor>
              </p>
            </div>
          ) : null}
        </section>
        <section className="flex flex-wrap justify-center gap-4">
          {person.approval.hasSufficientData ? (
            <>
              <Counter
                count={Math.round(person.approval.rating * 10) / 10} // Update to show one decimal point
                text="% Approval"
              />
              <div className="h-12 w-px bg-stone-200 dark:bg-stone-700"></div>
            </>
          ) : null}
          <Counter count={person.delivery.total} text="Letters" />
          {/* TODO: consider disabling this if none or not enough data */}
          {/* see http://localhost:3000/explore/person/ocd-person%2F80d93f41-e2ec-401d-8a60-ac98b09e82a0/Charles-Wesley-Clemons */}
          <figure className="hidden sm:block">
            <Chart
              data={person.delivery.chart}
              width={164}
              height={48}
              margin={4}
              color="#f59e0b"
              className="hidden sm:block"
            />
            <figcaption className="mt-2 text-center text-sm tracking-wide uppercase">
              Last {person.delivery.timeWindowDays}d
            </figcaption>
          </figure>
        </section>
      </div>

      <RangeSelector
        ranges={LETTER_TYPES.filter((type) => {
          // Show reviews tab for all officials if they have sufficient data
          if (type.id === REVIEWS_ID) {
            return person.approval.hasSufficientData;
          }
          return true;
        })}
        selectedRangeId={selectedRangeId}
        handleRangeChange={handleRangeChange}
      />
      <div className="flex flex-col gap-1">{selectedSet}</div>
    </>
  );
};

PersonPage.propTypes = {
  petitions: PropTypes.arrayOf(CampaignTypeList),
  openLetters: PropTypes.arrayOf(OpenLetterTypeList),
  person: PropTypes.shape({
    bioguideId: PropTypes.string.isRequired,
    titleAbbreviation: PropTypes.string,
    fullName: PropTypes.string.isRequired,
    stateCode: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    city: PropTypes.string,
    isFederalTarget: PropTypes.bool.isRequired,
    delivery: PropTypes.shape({
      chart: PropTypes.arrayOf(PropTypes.number),
      total: PropTypes.number,
      timeWindowDays: PropTypes.number,
    }),
    approval: PropTypes.shape({
      rating: PropTypes.number,
      hasSufficientData: PropTypes.bool.isRequired,
      pollVotes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          userId: PropTypes.string,
          pollId: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          direction: PropTypes.string,
          verifiedVoter: PropTypes.bool,
          reason: PropTypes.string,
          created: PropTypes.string,
        })
      ),
    }),
    amendmentCandidate: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default PersonPage;
